import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent, QuantityPackageDiscountEvent } from '@theme/events';

/**
 * A custom element that displays package cards based on quantity selection.
 * Automatically highlights the appropriate package card based on the current quantity.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement[]} packageCards - Array of package card elements
 * @property {HTMLElement} packagesWrapper - Container wrapper element
 * @property {HTMLElement} packagesContainer - Scrollable container element
 *
 * @extends {Component<Refs>}
 */
class QuantityPackagesComponent extends Component {
  /**
   * Required refs for this component
   */
  requiredRefs = ['packageCards', 'packagesWrapper', 'packagesContainer'];

  /**
   * Current quantity value
   * @type {number}
   */
  currentQuantity = 1;

  /**
   * Product ID for this quantity packages component
   * @type {string | null}
   */
  productId = null;

  /**
   * Called when the element is connected to the DOM.
   * Sets up event listeners for quantity changes.
   */
  connectedCallback() {
    super.connectedCallback();

    // Get product ID from closest product form
    const productForm = this.closest('product-form-component');
    if (productForm instanceof HTMLElement) {
      this.productId = productForm.dataset.productId || null;
    }

    // Find the quantity input in the product form
    this.quantityInput = this.#findQuantityInput();

    if (this.quantityInput) {
      // Set initial quantity
      this.currentQuantity = parseInt(this.quantityInput.value) || 1;
      this.#updateActiveCard(this.currentQuantity);

      // Listen for quantity selector updates
      document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate);

      // Listen for direct input changes
      this.quantityInput.addEventListener('input', this.#handleInputChange);
      this.quantityInput.addEventListener('change', this.#handleInputChange);
    }

    // Add click handlers to package cards
    this.refs.packageCards.forEach((card) => {
      card.addEventListener('click', this.#handleCardClick);
      card.addEventListener('keydown', this.#handleCardKeydown);
    });

    // Listen for variant updates (may change quantity rules)
    document.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
  }

  /**
   * Called when the element is disconnected from the DOM.
   * Cleans up event listeners.
   */
  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate);
    document.removeEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);

    if (this.quantityInput) {
      this.quantityInput.removeEventListener('input', this.#handleInputChange);
      this.quantityInput.removeEventListener('change', this.#handleInputChange);
    }

    this.refs.packageCards.forEach((card) => {
      card.removeEventListener('click', this.#handleCardClick);
      card.removeEventListener('keydown', this.#handleCardKeydown);
    });
  }

  /**
   * Finds the quantity input element in the closest product form.
   * @returns {HTMLInputElement | null}
   */
  #findQuantityInput() {
    const productForm = this.closest('product-form-component');
    if (!productForm) return null;

    const quantitySelector = productForm.querySelector('quantity-selector-component');
    if (!quantitySelector) return null;

    return quantitySelector.querySelector('input[name="quantity"]');
  }

  /**
   * Handles quantity selector update events.
   * @param {CustomEvent} event
   */
  #handleQuantityUpdate = (event) => {
    if (!(event instanceof QuantitySelectorUpdateEvent)) return;

    const quantity = event.detail.quantity;
    if (typeof quantity === 'number' && !isNaN(quantity)) {
      this.currentQuantity = quantity;
      this.#updateActiveCard(quantity);
    }
  };

  /**
   * Handles direct input changes on the quantity field.
   * @param {Event} event
   */
  #handleInputChange = (event) => {
    if (!(event.target instanceof HTMLInputElement)) return;

    const quantity = parseInt(event.target.value);
    if (!isNaN(quantity) && quantity > 0) {
      this.currentQuantity = quantity;
      this.#updateActiveCard(quantity);
    }
  };

  /**
   * Handles variant update events.
   * @param {CustomEvent} event
   */
  #handleVariantUpdate = (event) => {
    // Re-check quantity input in case it changed
    this.quantityInput = this.#findQuantityInput();

    if (this.quantityInput) {
      const quantity = parseInt(this.quantityInput.value);
      if (!isNaN(quantity) && quantity > 0) {
        this.currentQuantity = quantity;
        this.#updateActiveCard(quantity);
      }
    }
  };

  /**
   * Handles click events on package cards.
   * Updates the quantity input to match the card's minimum quantity.
   * @param {MouseEvent} event
   */
  #handleCardClick = (event) => {
    const card = event.currentTarget;
    if (!(card instanceof HTMLElement)) return;

    this.#selectPackage(card);
  };

  /**
   * Handles keyboard events on package cards.
   * @param {KeyboardEvent} event
   */
  #handleCardKeydown = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;

    event.preventDefault();
    const card = event.currentTarget;
    if (!(card instanceof HTMLElement)) return;

    this.#selectPackage(card);
  };

  /**
   * Selects a package by updating the quantity input.
   * @param {HTMLElement} card
   */
  #selectPackage(card) {
    const min = parseInt(card.dataset.min || '1');

    if (this.quantityInput && !isNaN(min)) {
      // Set the quantity to the minimum of the selected package
      this.quantityInput.value = min.toString();
      this.currentQuantity = min;

      // Trigger change event to update other components
      this.quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
      this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));

      // Update active card immediately
      this.#updateActiveCard(min);

      // Scroll card into view on mobile
      this.#scrollToCard(card);
    }
  }

  /**
   * Updates the active state of package cards based on quantity.
   * @param {number} quantity
   */
  #updateActiveCard(quantity) {
    let hasActiveCard = false;
    let activeDiscount = 0;
    let activeBadgeText = '';
    let activeSchemeColor = '';

    this.refs.packageCards.forEach((card) => {
      const min = parseInt(card.dataset.min || '0');
      const max = parseInt(card.dataset.max || '0');
      const discount = parseFloat(card.dataset.discount || '0');
      const badgeText = card.dataset.badgeText || '';
      const badgeColorScheme = card.dataset.badgeColorScheme || '';

      const isActive = quantity >= min && quantity <= max;

      if (isActive) {
        card.setAttribute('data-active', 'true');
        card.setAttribute('aria-selected', 'true');
        hasActiveCard = true;
        activeDiscount = discount;
        activeBadgeText = badgeText;
        activeSchemeColor = badgeColorScheme;

        // Scroll into view (works on both mobile and desktop)
        this.#scrollToCard(card);
      } else {
        card.setAttribute('data-active', 'false');
        card.setAttribute('aria-selected', 'false');
      }
    });

    // If no card is active (quantity outside all ranges), deactivate all
    if (!hasActiveCard) {
      this.refs.packageCards.forEach((card) => {
        card.setAttribute('data-active', 'false');
        card.setAttribute('aria-selected', 'false');
      });
      activeDiscount = 0;
      activeBadgeText = '';
      activeSchemeColor = '';
    }

    // Dispatch discount event if product ID is available
    if (this.productId) {
      document.dispatchEvent(
        new QuantityPackageDiscountEvent(
          quantity, 
          activeDiscount, 
          this.productId, 
          activeBadgeText,
          activeSchemeColor
        )
      );
    }
  }

  /**
   * Scrolls a card into view smoothly on both mobile and desktop.
   * @param {HTMLElement} card
   */
  #scrollToCard(card) {
    const container = this.refs.packagesContainer;
    if (!container) return;

    // Use scrollIntoView with smooth behavior
    requestAnimationFrame(() => {
      card.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      });
    });
  }
}

if (!customElements.get('quantity-packages-component')) {
  customElements.define('quantity-packages-component', QuantityPackagesComponent);
}
